/**
 * How often to ask whether a new version has been deployed.
 *
 * The installed app can sit in a dock for a week without ever navigating, and
 * a service worker only looks for a new version when something asks it to. So
 * something has to ask: on focus, which is when you come back to it and the
 * moment a stale version matters, and on a slow timer for the case where it
 * is left open and watched.
 *
 * Focus fires far more often than a deploy happens — every window switch —
 * so the checks are throttled. Without that, alt-tabbing would put a request
 * on the wire every time.
 */

/** Long enough that window-switching costs nothing, short enough to notice. */
export const MIN_CHECK_INTERVAL_MS = 60_000

/** Slow, because this only catches the app being left open and stared at. */
export const BACKGROUND_CHECK_INTERVAL_MS = 30 * 60_000

export function shouldCheckForUpdate({
  lastCheckedAt,
  now,
  minIntervalMs = MIN_CHECK_INTERVAL_MS,
}: {
  /** Null before the first check, which always goes ahead. */
  lastCheckedAt: number | null
  now: number
  minIntervalMs?: number
}): boolean {
  if (lastCheckedAt === null) return true
  // A clock that jumped backwards should not lock out checks until it catches
  // up; treat anything nonsensical as "long enough ago".
  if (now < lastCheckedAt) return true
  return now - lastCheckedAt >= minIntervalMs
}
